import { type ReactNode, type Ref, useId } from 'react';
import { cn } from '@/lib/utils';
import { type Crumb, PageHeading } from '../../PageHeading';
import type { BackLinkProps } from '../BackLink/BackLink';
import styles from './PageHeader.module.css';

export interface PageTitleProps {
    readonly children: ReactNode;
    /** The heading's ref — the focus-restoring hooks and the route's arrival focus move focus to it. */
    readonly headingRef?: Ref<HTMLHeadingElement> | undefined;
    /** The heading's id, kept where a browser spec or an `aria-labelledby` anchors on it (`leads-heading`). */
    readonly id?: string | undefined;
    /** The id of the element that describes the heading — PageHeader's description. */
    readonly 'aria-describedby'?: string | undefined;
    readonly 'data-testid'?: string | undefined;
}

/**
 * A screen's `h1`: the size every record page's title has (twice `type/body`, bold — design-system Rev 74) in
 * `text/heading`, with no margin (§6 *Screen header*; BUG-163). `PageHeader` draws it; a record page passes it to
 * `PageHeading` as its title, and a purpose-built header that carries more (the account page's marks and facts,
 * an editor's meta line) uses it on its own, so every screen title has one look.
 *
 * `tabIndex={-1}`: a focus target by script only — the arrival focus, and the focus a control that leaves the
 * page hands back. It joins no Tab order and draws no ring (`AppLayout.module.css`).
 */
export function PageTitle({
    children,
    headingRef,
    id,
    'aria-describedby': describedBy,
    'data-testid': testId,
}: PageTitleProps) {
    return (
        <h1
            aria-describedby={describedBy}
            className={styles.title}
            data-testid={testId}
            id={id}
            ref={headingRef}
            tabIndex={-1}
        >
            {children}
        </h1>
    );
}

/** `PageHeading`'s trail, current place and back arrow, for a screen header that has them. */
export interface PageHeaderBreadcrumb {
    readonly trail: readonly Crumb[];
    readonly current: string;
    readonly back: BackLinkProps;
    /** Prefixes the breadcrumb's test ids, as `PageHeading`'s own `data-testid` does. */
    readonly 'data-testid'?: string | undefined;
}

export interface PageHeaderProps {
    /** The screen's title — the `h1`'s text. */
    readonly title: ReactNode;
    readonly headingRef?: Ref<HTMLHeadingElement> | undefined;
    readonly headingId?: string | undefined;
    readonly headingTestId?: string | undefined;
    /**
     * One line beneath the title at `type/secondary` in `text/secondary`, which describes the heading
     * (`aria-describedby`). Required on a list screen (BUG-165).
     */
    readonly description?: ReactNode;
    readonly descriptionTestId?: string | undefined;
    /** The screen's actions, at the row's right end and centred against the title (BUG-164). */
    readonly actions?: ReactNode;
    /**
     * The §6 back navigation of a list below a section (Settings › Users): `PageHeading`'s breadcrumb above the
     * title row and its back arrow beside the title (design-system Rev 74).
     */
    readonly breadcrumb?: PageHeaderBreadcrumb | undefined;
    readonly 'data-testid'?: string | undefined;
}

/**
 * The screen header (design-system §6 *Screen header*; BUG-163, BUG-164, BUG-165): the title with the screen's
 * actions centred beside it, the optional one-line description beneath, and, on a list below a section, the
 * breadcrumb and back arrow of `PageHeading` (§6 *Back navigation*, Rev 74), with the description lined up under
 * the title rather than the arrow.
 *
 * It renders a `<header>`, which may not hold another: a screen whose header area holds more (the Email page's
 * search box) wraps it in a plain box, not a second `<header>`.
 */
export function PageHeader({
    title,
    headingRef,
    headingId,
    headingTestId,
    description,
    descriptionTestId,
    actions,
    breadcrumb,
    'data-testid': testId,
}: PageHeaderProps) {
    const descriptionId = useId();
    const hasDescription = description !== undefined && description !== null;
    const heading = (
        <PageTitle
            aria-describedby={hasDescription ? descriptionId : undefined}
            data-testid={headingTestId}
            headingRef={headingRef}
            id={headingId}
        >
            {title}
        </PageTitle>
    );
    const row = (
        <div className={styles.row}>
            {heading}
            {actions === undefined || actions === null ? null : (
                <div className={styles.actions}>{actions}</div>
            )}
        </div>
    );
    return (
        <header className={styles.header} data-testid={testId}>
            <div className={styles.titleBlock}>
                {breadcrumb === undefined ? (
                    row
                ) : (
                    <PageHeading
                        back={breadcrumb.back}
                        current={breadcrumb.current}
                        trail={breadcrumb.trail}
                        {...(breadcrumb['data-testid'] === undefined
                            ? {}
                            : { 'data-testid': breadcrumb['data-testid'] })}
                    >
                        {row}
                    </PageHeading>
                )}
                {hasDescription ? (
                    <p
                        className={cn(
                            styles.description,
                            breadcrumb === undefined ? undefined : styles.besideArrow,
                        )}
                        data-testid={descriptionTestId}
                        id={descriptionId}
                    >
                        {description}
                    </p>
                ) : null}
            </div>
        </header>
    );
}
